/**
 * B526 (e4) leg (f1) — the OCTO wire. An OCTO partner calls OCTO's own verbs
 * and paths (docs.octo.travel), so a CLOSED table maps each route onto one
 * partner-facing octo.* operation. It is data the authorizer (which op, which
 * tenant), the ops entry (how to translate) and the stack (which routes
 * exist) all read — three readers, one table, no drift. Not an operation
 * module: the registry does not collect it.
 *
 * `idParam` names the path parameter the op takes; `fields` the body fields
 * kept (OCTO clients send more, and contracts are strict); `key` the write's
 * idempotency key source (the partner's booking uuid — a retry replays).
 */
export type OctoWireUnwrap = "products" | "product" | "availabilities" | "days" | "booking";

export interface OctoWireRoute {
  readonly routeKey: string;
  readonly operation: string;
  readonly unwrap: OctoWireUnwrap;
  readonly idParam?: "productId" | "uuid";
  readonly fields?: readonly string[];
  readonly key?: "reserve" | "confirm" | "cancel";
}

export const OCTO_WIRE_ROUTES: readonly OctoWireRoute[] = Object.freeze([
  { routeKey: "GET /octo/products", operation: "octo.products", unwrap: "products" },
  { routeKey: "GET /octo/products/{productId}", operation: "octo.products", unwrap: "product", idParam: "productId" },
  {
    routeKey: "POST /octo/availability",
    operation: "octo.availability",
    unwrap: "availabilities",
    fields: ["productId", "optionId", "localDateStart", "localDateEnd", "availabilityIds"],
  },
  {
    routeKey: "POST /octo/availability/calendar",
    operation: "octo.availability.calendar",
    unwrap: "days",
    fields: ["productId", "optionId", "localDateStart", "localDateEnd"],
  },
  {
    routeKey: "POST /octo/bookings",
    operation: "octo.booking.reserve",
    unwrap: "booking",
    fields: ["uuid", "productId", "optionId", "availabilityId", "expirationMinutes", "notes", "currency", "unitItems"],
    key: "reserve",
  },
  { routeKey: "GET /octo/bookings/{uuid}", operation: "octo.booking.get", unwrap: "booking", idParam: "uuid" },
  {
    routeKey: "POST /octo/bookings/{uuid}/confirm",
    operation: "octo.booking.confirm",
    unwrap: "booking",
    idParam: "uuid",
    fields: ["contact", "resellerReference"],
    key: "confirm",
  },
  { routeKey: "DELETE /octo/bookings/{uuid}", operation: "octo.booking.cancel", unwrap: "booking", idParam: "uuid", fields: ["reason"], key: "cancel" },
  { routeKey: "POST /octo/bookings/{uuid}/cancel", operation: "octo.booking.cancel", unwrap: "booking", idParam: "uuid", fields: ["reason"], key: "cancel" },
]);

const BY_KEY = new Map(OCTO_WIRE_ROUTES.map((r) => [r.routeKey, r]));

export function octoWireRouteFor(routeKey: string): OctoWireRoute | undefined {
  return BY_KEY.get(routeKey);
}
